import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, Typography, Space, Button, Tag, Input, ColorPicker, App, Select, Divider, Collapse } from 'antd';

const { Title } = Typography;

const KNOWN_LANGS = [
    { key: 'de', label: 'Deutsch' },
    { key: 'en', label: 'English' },
    { key: 'fr', label: 'Français' },
    { key: 'it', label: 'Italiano' },
    { key: 'pt', label: 'Português' },
    { key: 'nl', label: 'Nederlands' },
    { key: 'tr', label: 'Türkçe' },
    { key: 'ar', label: 'العربية' },
];

const AdminSettings = () => {
    const { message } = App.useApp();
    const [langs, setLangs] = useState(['de','en','fr','it']);
    const [primary, setPrimary] = useState('#1677ff');
    const [primaryHover, setPrimaryHover] = useState('#3c8cff');
    const [secondary, setSecondary] = useState('#13c2c2');
    const [secondaryHover, setSecondaryHover] = useState('#36cfc9');

    const [newCode, setNewCode] = useState('');
    const [newLabel, setNewLabel] = useState('');
    const [maxMb, setMaxMb] = useState(1024);
    // Categories: [{ key, labels: { en:'', de:'', ... } }]
    const [categories, setCategories] = useState([]);
    const [newCatKey, setNewCatKey] = useState('');

    const load = async () => {
        try {
            console.log('[AdminSettings] Loading languages & theme...');
            const [l, t, vu, vc] = await Promise.all([
                axios.get('/v1/settings/languages'),
                axios.get('/v1/settings/theme').catch(() => ({ data: { data: { primary: '#1677ff' } } })),
                axios.get('/v1/settings/video-upload').catch(() => ({ data: { data: { max_mb: 1024 } } })),
                axios.get('/v1/settings/video-categories').catch(() => ({ data: { data: [] } }))
            ]);
            const loadedLangs = l.data?.data || langs;
            const theme = t.data?.data || {};
            const vuCfg = vu.data?.data || { max_mb: 1024 };
            console.log('[AdminSettings] Loaded', { languages: loadedLangs, theme, videoUpload: vuCfg, videoCategories: vc.data?.data });
            setLangs(loadedLangs);
            setPrimary(theme.primary || '#1677ff');
            setPrimaryHover(theme.primary_hover || '#3c8cff');
            setSecondary(theme.secondary || '#13c2c2');
            setSecondaryHover(theme.secondary_hover || '#36cfc9');
            setMaxMb(vuCfg.max_mb || 1024);
            setCategories(vc.data?.data || []);
        } catch (e) {}
    };

    useEffect(() => { load(); }, []);

    const removeLang = (key) => {
        setLangs((prev) => prev.filter(k => k !== key));
    };

    const addLang = () => {
        const code = (newCode || '').trim().toLowerCase();
        if (!code) return;
        if (langs.includes(code)) {
            message.info('Language already added');
            return;
        }
        setLangs([...langs, code]);
        setNewCode('');
        setNewLabel('');
    };

    const save = async () => {
        try {
            console.log('[AdminSettings] Saving...', { languages: langs, theme: { primary, primary_hover: primaryHover, secondary, secondary_hover: secondaryHover } });
            // Sanitize categories data to ensure all values are strings
            const sanitizedCategories = categories.map(cat => ({
                key: String(cat.key || ''),
                labels: Object.fromEntries(
                    Object.entries(cat.labels || {}).map(([locale, label]) => [
                        String(locale),
                        String(label || '')
                    ])
                )
            }));
            console.log('[AdminSettings] Categories payload:', { categories: sanitizedCategories });
            const r1 = await axios.post('/v1/settings/languages', { languages: langs });
            const r2 = await axios.post('/v1/settings/theme', { primary, primary_hover: primaryHover, secondary, secondary_hover: secondaryHover });
            const r3 = await axios.post('/v1/settings/video-upload', { max_mb: maxMb });
            const r4 = await axios.post('/v1/settings/video-categories', { categories: sanitizedCategories });
            console.log('[AdminSettings] Saved responses', { languages: r1?.data, theme: r2?.data, videoUpload: r3?.data, videoCategories: r4?.data });
            message.success('Settings saved');
            // Broadcast theme change so the app updates without full reload
            window.dispatchEvent(new CustomEvent('theme-updated', { detail: r2?.data?.data || { primary, primary_hover: primaryHover, secondary, secondary_hover: secondaryHover } }));
        } catch (e) {
            console.error('[AdminSettings] Save failed', e?.response?.data || e);
            message.error('Save failed');
        }
    };

    const labelFor = (code) => KNOWN_LANGS.find(l => l.key === code)?.label || code.toUpperCase();

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Title level={2} style={{ margin: 0 }}>Admin Settings</Title>

                <Card title="Languages" variant="outlined">
                    <Space wrap style={{ marginBottom: 12 }}>
                        {langs.map(code => (
                            <Tag
                                key={code}
                                color="blue"
                                closable
                                onClose={(e) => { e.preventDefault(); removeLang(code); }}
                                style={{ padding: '6px 10px' }}
                            >
                                {labelFor(code)} ({code})
                            </Tag>
                        ))}
                    </Space>
                    <Space align="start" wrap>
                        <Input placeholder="Code (e.g. en)" style={{ width: 140 }} value={newCode} onChange={(e) => setNewCode(e.target.value)} />
                        <Input placeholder="Label (optional)" style={{ width: 200 }} value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
                        <Button type="dashed" onClick={addLang}>Add language</Button>
                    </Space>
                    <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                        Tip: You can also click the tags' close button to remove a language.
                    </div>
                </Card>

                <Card title="Theme" variant="outlined">
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span>Primary color</span>
                            <ColorPicker value={primary} onChange={(c) => setPrimary(c.toHexString())} />
                            <span>Hover</span>
                            <ColorPicker value={primaryHover} onChange={(c) => setPrimaryHover(c.toHexString())} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span>Secondary color</span>
                            <ColorPicker value={secondary} onChange={(c) => setSecondary(c.toHexString())} />
                            <span>Hover</span>
                            <ColorPicker value={secondaryHover} onChange={(c) => setSecondaryHover(c.toHexString())} />
                        </div>
                    </Space>
                </Card>

                <Card title="Video Upload" variant="outlined">
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span>Max upload size (MB)</span>
                            <Input type="number" min={10} max={20480} value={maxMb} onChange={(e) => setMaxMb(parseInt(e.target.value || '0', 10))} style={{ width: 160 }} />
                        </div>
                        <div style={{ color: '#999', fontSize: 12 }}>
                            We try to apply limits via .user.ini automatically. Some hosts require manual changes in php.ini or server config.
                        </div>
                        <Button onClick={async () => {
                            try {
                                const res = await axios.get('/v1/settings/php-upload-limits');
                                const d = res.data?.data || {};
                                message.info(`PHP upload_max_filesize=${d.upload_max_filesize}, post_max_size=${d.post_max_size}`);
                            } catch {}
                        }}>Check current PHP limits</Button>
                    </Space>
                </Card>

                <Card title="Video Categories" variant="outlined">
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <div>
                            <Space wrap>
                                {categories.map((c) => (
                                    <Tag key={c.key} color="blue" closable onClose={(e) => { e.preventDefault(); setCategories(categories.filter(x => x.key !== c.key)); }}>{c.labels?.en || c.key}</Tag>
                                ))}
                            </Space>
                            <div style={{ color: '#999', fontSize: 12, marginTop: 6 }}>Click the close icon to remove a category.</div>
                        </div>
                        <Divider style={{ margin: '8px 0' }} />
                        <div>
                            <Space>
                                <Input placeholder="New key (e.g. tutorial)" value={newCatKey} onChange={(e) => setNewCatKey(e.target.value)} style={{ width: 240 }} />
                                <Button onClick={() => {
                                    const key = (newCatKey||'').trim().toLowerCase();
                                    if (!key) return;
                                    if (categories.some(c => c.key === key)) { message.info('Category exists'); return; }
                                    setCategories([...categories, { key, labels: { en: key.charAt(0).toUpperCase()+key.slice(1) } }]);
                                    setNewCatKey('');
                                }}>Add</Button>
                            </Space>
                            <div style={{ color: '#999', fontSize: 12, marginTop: 6 }}>Keys are stable identifiers; labels are translated below.</div>
                        </div>
                        <Divider style={{ margin: '8px 0' }} />
                        {categories.length > 0 && (
                            <div>
                                <Title level={5} style={{ marginTop: 0 }}>Translate labels</Title>
                                <Collapse
                                    accordion
                                    items={(categories || []).map((cat, idx) => ({
                                        key: cat.key,
                                        label: (cat.labels?.en || cat.key),
                                        extra: (
                                            <Button size="small" onClick={async (e) => {
                                                e.stopPropagation();
                                                // Optimistic save to ensure latest edits are persisted
                                                try { await axios.post('/v1/settings/video-categories', { categories }); } catch {}
                                                try {
                                                    const res = await axios.post('/v1/settings/video-categories/translate', {
                                                        key: cat.key,
                                                        // let backend auto-detect source and targets
                                                    });
                                                    const updated = res.data?.data;
                                                    setCategories(prev => prev.map(c => c.key === cat.key ? updated : c));
                                                    message.success('Translated');
                                                } catch { message.error('Translate failed'); }
                                            }}>AI Translate</Button>
                                        ),
                                        children: (
                                            <Space wrap>
                                                {(langs || []).map((lc) => (
                                                    <div key={`${cat.key}-${lc}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span style={{ width: 60, textAlign: 'right', color: '#666' }}>{lc.toUpperCase()}</span>
                                                        <Input
                                                            placeholder={`Label in ${lc}`}
                                                            value={(categories[idx]?.labels?.[lc]) || ''}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                setCategories(prev => prev.map((c, i) => i === idx ? { ...c, labels: { ...(c.labels || {}), [lc]: val } } : c));
                                                            }}
                                                            style={{ width: 260 }}
                                                        />
                                                    </div>
                                                ))}
                                            </Space>
                                        ),
                                    }))}
                                />
                            </div>
                        )}
                        <div style={{ color: '#999', fontSize: 12 }}>These categories appear in Admin Videos and the Videos grid filters, localized by the viewer's language.</div>
                    </Space>
                </Card>

                <div>
                    <Button type="primary" onClick={save}>Save settings</Button>
                </div>
            </Space>
        </div>
    );
};

export default AdminSettings;
